import { isCreateEntry, withCreateLabel } from "ui/creatable-select/creatable-select.helpers";
import { useCreatableSelect } from "ui/creatable-select/use-creatable-select";
import { Select } from "ui/select/select";
import { SelectOptionContent } from "ui/select/components/select-option-content";

import type { CreatableSelectProps } from "ui/creatable-select/creatable-select.types";
import type { FormatOptionLabelMeta, OptionType } from "ui/select/select.types";

/**
 * `Select` plus a `Create '…'` entry for typed text no option names, compared case-insensitively. The
 * entry is a real option, last in the list, so it filters like the rest; picking it calls
 * `onCreateOption` and leaves `onChange` alone. The state behind it is `useCreatableSelect`.
 *
 * `autoHighlight` is what lets Enter create: with no other match the entry is the only option, so it is
 * the highlighted one. With matches the first of them is highlighted, so Enter on `Bet` picks `Beta`
 * rather than creating a near-twin — clicking the entry still creates `Bet`.
 */
export const CreatableSelect = <Option extends OptionType = OptionType>({
  options,
  onChange,
  onCreateOption,
  formatOptionLabel,
  filterOption,
  ...selectProps
}: CreatableSelectProps<Option>) => {
  const { listedOptions, keepOption, handleChange, handleInputChange } = useCreatableSelect<Option>({
    options,
    onChange,
    onCreateOption,
    filterOption,
  });

  const renderOptionLabel = (option: Option, meta: FormatOptionLabelMeta) => {
    if (isCreateEntry(option)) {
      return <SelectOptionContent option={withCreateLabel(option)} />;
    }

    return formatOptionLabel ? (
      formatOptionLabel(option, meta)
    ) : (
      <SelectOptionContent option={option} />
    );
  };

  return (
    <Select<Option>
      {...selectProps}
      autoHighlight
      filterOption={keepOption}
      formatOptionLabel={renderOptionLabel}
      onChange={handleChange}
      onInputChange={handleInputChange}
      options={listedOptions}
    />
  );
};
